/**
 * ニュースのセクション末尾に新規セクションを追加するボタン。
 * メール用 AddSectionButton の LINE ニュース版（image セクション含む）
 * 仕様書: docs/01_spec/spec_line_bot.md A-10.5
 */
import { Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { LineNewsSectionType } from '@goenhub/shared';

interface Props {
  onAdd: (type: LineNewsSectionType) => void;
}

const SECTION_OPTIONS: Array<{ type: LineNewsSectionType; label: string; desc: string }> = [
  { type: 'greeting', label: 'あいさつ', desc: '冒頭の挨拶' },
  { type: 'body', label: '本文', desc: 'メインメッセージ' },
  { type: 'image', label: '画像', desc: 'LINE 画像メッセージ' },
  { type: 'cta', label: 'CTA', desc: '誘導リンク' },
  { type: 'closing', label: '締め', desc: '末尾の挨拶' },
];

export function AddNewsSectionButton({ onAdd }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded border border-dashed border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--accent)] hover:text-[var(--accent)] hover:bg-[var(--accent-subtle)] transition-colors text-xs"
      >
        <Plus className="size-3.5" />
        セクションを追加
      </button>
      {open && (
        <div className="absolute left-0 right-0 mt-1 z-20 rounded border border-[var(--border)] bg-[var(--bg-surface)] shadow-lg overflow-hidden">
          {SECTION_OPTIONS.map((opt) => (
            <button
              key={opt.type}
              type="button"
              onClick={() => {
                onAdd(opt.type);
                setOpen(false);
              }}
              className="w-full flex items-baseline gap-2 px-3 py-2 text-left hover:bg-[var(--bg-hover)] transition-colors"
            >
              <span className="text-[13px] font-medium text-[var(--text-primary)]">
                {opt.label}
              </span>
              <span className="text-[10px] text-[var(--text-muted)]">{opt.desc}</span>
              <code className="ml-auto text-[10px] font-mono text-[var(--text-muted)]">
                {opt.type}
              </code>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
